import { getTeamIcon } from '@/utils/teamIcons';

interface Team {
  id: string;
  name: string;
  iconName: string;
  iconColor: string;
  totalScore: number;
}

type PositionChange = 'up' | 'down' | 'none';

interface DisplayPodiumProps {
  teams: Team[];
  pointsLabel: string;
  positionChanges?: Map<string, PositionChange>;
}

/**
 * Top-3 podium in the deck language: three surface cards with serif
 * rank numerals, staggered by height. First place carries the accent —
 * a top rule and accent rank — instead of medal golds.
 */
const PODIUM_CONFIG = [
  { position: 2, order: 1, height: 'min-h-[7rem]', rank: 'text-5xl' },
  { position: 1, order: 2, height: 'min-h-[9rem]', rank: 'text-7xl' },
  { position: 3, order: 3, height: 'min-h-[6rem]', rank: 'text-5xl' },
] as const;

export function DisplayPodium({ teams, pointsLabel, positionChanges }: DisplayPodiumProps) {
  const hasMoved = (teamId: string) => {
    const change = positionChanges?.get(teamId);
    return change && change !== 'none';
  };

  return (
    <div className="flex items-end justify-center gap-3 px-6 pb-2 pt-4">
      {PODIUM_CONFIG.map((config) => {
        const team = teams[config.position - 1];
        const isFirst = config.position === 1;

        if (!team) {
          return (
            <div
              key={config.position}
              className={`flex flex-1 flex-col items-center justify-end rounded-2xl border border-dashed p-4 opacity-40 ${config.height}`}
              style={{ order: config.order, borderColor: 'var(--dsp-line)' }}
            >
              <span
                className={`font-semibold tabular-nums ${config.rank}`}
                style={{ color: 'var(--dsp-soft)' }}
              >
                {config.position}
              </span>
            </div>
          );
        }

        const TeamIcon = getTeamIcon(team.iconName);

        return (
          <div
            key={config.position}
            className={`dsp-animated flex flex-1 flex-col items-center justify-end gap-1.5 rounded-2xl border p-4 ${config.height}`}
            style={{
              order: config.order,
              backgroundColor: 'var(--dsp-surface)',
              borderColor: 'var(--dsp-line)',
              borderTop: isFirst ? '4px solid var(--dsp-accent)' : undefined,
              animation: hasMoved(team.id) ? 'slowBlink 1.5s ease-in-out' : undefined,
            }}
          >
            <div
              className="flex h-12 w-12 items-center justify-center rounded-full"
              style={{ backgroundColor: team.iconColor }}
            >
              <TeamIcon className="h-6 w-6 text-white" />
            </div>

            <span className="max-w-full truncate text-sm font-semibold">{team.name}</span>

            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl font-semibold tabular-nums">
                {team.totalScore.toLocaleString()}
              </span>
              <span
                className="font-mono text-[0.625rem] uppercase tracking-[0.15em]"
                style={{ color: 'var(--dsp-soft)' }}
              >
                {pointsLabel}
              </span>
            </div>

            <span
              className={`font-semibold leading-none tabular-nums ${config.rank}`}
              style={{ color: isFirst ? 'var(--dsp-accent-text)' : 'var(--dsp-soft)' }}
            >
              {config.position}
            </span>
          </div>
        );
      })}
    </div>
  );
}
